// Offsets refer to the original editor text, so repeated question numbers are safe.
export const getHandoutQuestions = (value) => {
  const text = String(value || "");
  const headings = [];
  const numberedHeadings = [];
  const separators = [];
  let offset = 0;
  let fence = "";
  let page; let fileIndex = 0;
  for (const line of text.split("\n")) {
    const pageMarker = !fence && line.match(/^---\s*Page\s+(\d+)\s*---\s*$/i);
    if (pageMarker) { const nextPage = Number(pageMarker[1]); if (page && nextPage <= page) fileIndex += 1; page = nextPage; }
    const marker = line.trim().match(/^(`{3,}|~{3,})/);
    if (marker) {
      if (!fence) fence = marker[1][0];
      else if (marker[1][0] === fence) fence = "";
    } else if (!fence) {
      const heading = line.match(/^\s*(?:#{1,6}\s*)?(?:\*\*)?第\s*([\d一二三四五六七八九十百]+)\s*題/);
      if (heading) headings.push({ start: offset, label: `第 ${heading[1]} 題`, number: heading[1], page, fileIndex });
      if (!heading) {
        const numbered = line.match(/^\s*(?:#{1,6}\s*)?(?:\*\*)?(\d{1,3})[.．、)）](?:\*\*)?(?:\s+.*)?$/);
        if (numbered) numberedHeadings.push({ start: offset, label: `第 ${numbered[1]} 題`, number: numbered[1], page, fileIndex });
      }
      if (/^\s*---+\s*$/.test(line)) separators.push({ start: offset, end: offset + line.length + 1 });
    }
    offset += line.length + 1;
  }
  const selectedHeadings = headings.length ? headings : numberedHeadings;
  const ranges = !selectedHeadings.length && separators.length
    ? [0, ...separators.map((item) => item.end)].map((start, i) => ({ start, end: separators[i]?.start ?? text.length }))
    : selectedHeadings.map((item, i) => ({ ...item, end: selectedHeadings[i + 1]?.start ?? text.length }));
  return ranges.filter(({ start, end }) => text.slice(start, end).trim()).map((item, index) => ({
    ...item,
    label: item.label || `第 ${index + 1} 區塊`,
    preview: text.slice(item.start, item.end).replace(/\s+/g, " ").slice(0, 90),
  }));
};

export const attachHandoutFigure = (text, question, role, figure) => {
  const block = text.slice(question.start, question.end);
  const snippet = figure.kind === "text"
    ? `${role}：\n${figure.text.trim()}`
    : figure.kind === "python"
      ? `圖形程式碼（${role}）：\n\`\`\`python\n${figure.code.trim()}\n\`\`\``
      : `圖形原圖（${role}）：\n![附圖](${figure.url})`;
  const placeholder = new RegExp(`\\[附圖待處理[（(]${role}[）)][：:][^\\]\\n]*\\]`);
  const pageMarkers = [...block.matchAll(/^---\s*Page\s+\d+\s*---\s*$/gim)];
  const lastPageMarker = pageMarkers[pageMarkers.length - 1];
  const pageIntro = lastPageMarker && block.slice(lastPageMarker.index + lastPageMarker[0].length)
    .split("\n").every((line) => {
      const value = line.trim();
      return !value || /^說明\s*[：:]/.test(value) ||
        /^[一二三四五六七八九十]+\s*[、.．]/.test(value) ||
        /(?:學年度|學期).*(?:考|試)|(?:期中|期末|段考)/.test(value);
    });
  const updated = placeholder.test(block)
    ? block.replace(placeholder, () => snippet)
    : pageIntro
      ? `${block.slice(0, lastPageMarker.index).trimEnd()}\n\n${snippet}\n\n${block.slice(lastPageMarker.index)}`
      : `${block.trimEnd()}\n\n${snippet}\n\n`;
  return text.slice(0, question.start) + updated + text.slice(question.end);
};

export const extractOriginalFigureAssets = (text, fallbackRole = "prompt") => {
  const assets = [];
  const cleaned = text.replace(
    /(?:^|\n)\s*圖形原圖[（(](題目|答案|詳解)[）)]\s*[：:]\s*\n!\[([^\]\n]*)\]\((https:\/\/[^\s)]+)\)/g,
    (original, label, alt, url) => {
      let parsed;
      try { parsed = new URL(url); } catch { return original; }
      if (parsed.protocol !== "https:" || parsed.hostname !== "assets.sunnytseng.com" || parsed.username || parsed.password) return original;
      let key;
      try { key = decodeURIComponent(parsed.pathname.slice(1)); } catch { return original; }
      assets.push({
        role: ({ 題目: "prompt", 答案: "answer", 詳解: "solution" })[label] || fallbackRole,
        url, storage_key: key, alt_text: alt || "附圖", mime_type: "image/png",
        source_language: "", source_code: "", marking_guide: "", sort_order: assets.length,
      });
      return "";
    },
  );
  return { text: cleaned, assets };
};


// UI IDs are independent of printed numbers, which can repeat by page or section.
export const reconcileHandoutQuestionIds = (text, previous, makeId) => {
  const used = new Set();
  return getHandoutQuestions(text).map((question) => {
    const signature = text.slice(question.start, question.end)
      .replace(/圖形程式碼[（(](?:題目|答案|詳解)[）)]：\s*```python[\s\S]*?```/g, "")
      .replace(/圖形原圖[（(](?:題目|答案|詳解)[）)]：[\s\S]*?!\[[^\]]*\]\([^)]*\)/g, "")
      .replace(/\[附圖待處理[^\]\n]*\]/g, "").replace(/\s+/g, " ").trim();
    const existing = previous.find((item) => !used.has(item.id) && item.signature === signature);
    if (existing) used.add(existing.id);
    return { ...question, signature, id: existing?.id || makeId() };
  });
};

const matchText = (text) => String(text || "").replace(/第\s*[0-9一二三四五六七八九十百]+\s*題|^\s*\d+[.．、)]/g, "")
  .replace(/題目\s*[:：]|[^\p{L}\p{N}]/gu, "");

export const matchFigureQuestionId = (questions, candidate, fileCount = 1) => {
  const hint = matchText(candidate.question_text);
  if (hint.length < 8) return "";
  const matches = questions.filter((question) => {
    if (question.page && question.page !== candidate.page) return false;
    if (fileCount > 1 && (question.page === undefined || question.fileIndex !== candidate.fileIndex)) return false;
    const prompt = matchText(question.signature || question.preview);
    return prompt.includes(hint) || (prompt.length >= 12 && hint.includes(prompt));
  });
  return matches.length === 1 ? matches[0].id : "";
};

export const attachHandoutFigures = (text, entries, questions) => {
  const grouped = new Map();
  entries.forEach((entry) => {
    const question = questions.find((item) => item.id === entry.questionId);
    if (!question) throw new Error("題目已變更，請重新核對附圖所屬題目。");
    if (!grouped.has(question.start)) grouped.set(question.start, { question, entries: [] });
    grouped.get(question.start).entries.push(entry);
  });
  let updated = text;
  [...grouped.values()].sort((a, b) => b.question.start - a.question.start).forEach(({ question, entries: figures }) => {
    let block = text.slice(question.start, question.end);
    figures.forEach(({ role, figure }) => { block = attachHandoutFigure(block, { start: 0, end: block.length }, role, figure); });
    updated = updated.slice(0, question.start) + block + updated.slice(question.end);
  });
  return updated;
};
